import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { DescriptionOutlined } from '@mui/icons-material';
import { useGetTemplatePreviewUrl } from '../../../hooks/useGetTemplatePreviewUrl';
import { Modal } from '../../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import { DocumentPreviewModalConfig } from '../../../../../../../components/fileUploader/DocumentPreviewModal';
import SimpleTooltip from '../../../../../../../components/SimpleTooltip';
import type { CoverageFormInfo } from '../../../api/types';
import type { Nullable } from '../../../../../../../types/common/utility';

/**
 * The preview modal renders a spinner; without a floor the spinner flashes for a
 * cached form, which reads as a glitch rather than as loading.
 */
const PREVIEW_MIN_LOADING_MS = 1500;

interface CoverageFormPreviewProps {
  readonly coverageForm: Nullable<CoverageFormInfo>;
  readonly isLoading: boolean;
  readonly productKey: string;
}

/**
 * Opens the form wording behind an endorsement. Shared by the ZSP component-based listing
 * and the root-category listing every other product uses (FIR-21152).
 *
 * Deliberately a *document* icon, not the generic info icon: this sits immediately beside
 * the coverage-description info icon, and two identical icons gave no clue which one showed
 * form wording. The icon is itself the button so reading a form is one click rather than
 * hover-then-hit-a-second-target inside the tooltip.
 *
 * Renders nothing when the endorsement has no form on file — an absent form is not an error
 * state for the underwriter.
 */
const CoverageFormPreview = ({
  coverageForm,
  isLoading,
  productKey,
}: CoverageFormPreviewProps) => {
  // The guard sits outside the hook-using component on purpose: most coverage rows
  // have no form, and mounting a mutation for every one of them would make the whole
  // listing depend on a QueryClient it otherwise never needs.
  if (!isLoading && !coverageForm?.formNumber) {
    return null;
  }

  return (
    <CoverageFormPreviewAffordance
      coverageForm={coverageForm}
      productKey={productKey}
    />
  );
};

const CoverageFormPreviewAffordance = ({
  coverageForm,
  productKey,
}: Omit<CoverageFormPreviewProps, 'isLoading'>) => {
  const previewForm = useGetTemplatePreviewUrl();

  const handlePreviewForm = () => {
    Modal.show(DocumentPreviewModalConfig, {
      data: {
        onFetch: async () => {
          const minLoadingTime = new Promise((resolve) =>
            setTimeout(resolve, PREVIEW_MIN_LOADING_MS)
          );
          const mutationPromise = previewForm.mutateAsync({
            pdfCatalogId: coverageForm?.pdfCatalogId,
            productKey,
          });

          const [result] = await Promise.all([mutationPromise, minLoadingTime]);
          return result.previewUrl;
        },
      },
    });
  };

  return (
    <SimpleTooltip
      arrow
      placement="bottom"
      enterDelay={200}
      title={
        <Box pr={1}>
          <Typography variant="subtitle2">View form wording</Typography>
          <Typography variant="caption">{coverageForm?.formNumber}</Typography>
        </Box>
      }
    >
      <IconButton
        size="small"
        aria-label="View form wording"
        onClick={handlePreviewForm}
        sx={{ padding: '0.15rem', marginLeft: '0.15rem' }}
      >
        <DescriptionOutlined fontSize="small" />
      </IconButton>
    </SimpleTooltip>
  );
};

export default CoverageFormPreview;
